import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import CaseHeader from '../components/admin/case/CaseHeader';
import OverviewTab from '../components/admin/case/tabs/OverviewTab';
import ImagesTab from '../components/admin/case/tabs/ImagesTab';
import DocumentsTab from '../components/admin/case/tabs/DocumentsTab';
import RequestsTab from '../components/admin/case/tabs/RequestsTab';
import ProcessingTab from '../components/admin/case/tabs/ProcessingTab';
import ReportTab from '../components/admin/case/tabs/ReportTab';
import WorkflowTab from '../components/admin/case/tabs/WorkflowTab';
import ProvisionTab from '../components/admin/case/tabs/ProvisionTab';
import BeteiligteTab from '../components/admin/case/tabs/BeteiligteTab';

const AdminCaseDetailPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [gateStatus, setGateStatus] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadCase();
    loadGateStatus();
  }, [caseId, token]);

  const loadCase = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      // API returns {case, partner, customer, lawyer, uploads}
      // Keep the structure as-is since the component expects data.case
      setData(result);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGateStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/gate/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setGateStatus(result);
    } catch (error) {
      console.error('Gate status load failed:', error);
    }
  };

  if (loading || !data) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Laden...</div>;
  }

  const caseData = data.case || {};
  const partner = data.partner || {};
  const requests = caseData.requests || [];
  const timeline = caseData.timeline || [];

  const TABS = [
    { key: 'overview', label: 'Überblick', component: <OverviewTab caseData={caseData} partner={partner} /> },
    { key: 'beteiligte', label: 'Beteiligte', component: <BeteiligteTab caseId={caseId} caseData={caseData} onRefresh={loadCase} token={token} /> },
    { key: 'workflow', label: 'Workflow', component: <WorkflowTab caseId={caseId} caseData={caseData} timeline={timeline} onRefresh={loadCase} token={token} /> },
    { key: 'bearbeitung', label: 'Bearbeitung', component: <ProcessingTab caseId={caseId} caseData={caseData} onRefresh={loadCase} token={token} /> },
    { key: 'requests', label: `Rückfragen ${requests.filter(r => r.status === 'open').length > 0 ? `(${requests.filter(r => r.status === 'open').length})` : ''}`, component: <RequestsTab caseId={caseId} requests={requests} onRefresh={loadCase} token={token} /> },
    { key: 'gutachten', label: 'Gutachten', component: <ReportTab caseId={caseId} caseData={caseData} onRefresh={loadCase} token={token} /> },
    { key: 'images', label: 'Bilder', component: <ImagesTab caseData={caseData} /> },
    { key: 'documents', label: 'Dokumente', component: <DocumentsTab caseData={caseData} /> },
    { key: 'provision', label: 'Provision', component: <ProvisionTab caseId={caseId} caseData={caseData} partner={partner} onRefresh={loadCase} token={token} /> }
  ];

  const activeTabComponent = TABS.find(t => t.key === activeTab)?.component;

  return (
    <div className="min-h-screen bg-gray-50">
      <CaseHeader caseData={caseData} partner={partner} />

      {/* M11.4: Document Gate Warning */}
      {gateStatus && !gateStatus.documents_ready_for_processing && (
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-900">Fall blockiert – Pflichtdokumente fehlen</h3>
                <p className="text-sm text-red-700">
                  {gateStatus.missing_required_docs?.map(doc => 
                    doc === 'abtretung' ? 'Abtretungserklärung' : 'Vollmacht Anwalt'
                  ).join(', ')} muss vom Partner unterschrieben werden.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Status Badges */}
      {gateStatus && (
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-center space-x-3 text-sm">
            <span className="text-gray-600">Dokumente:</span>
            <div className={`px-3 py-1 rounded-full ${gateStatus.abtretung_signed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {gateStatus.abtretung_signed ? '✓' : '✗'} Abtretung
            </div>
            {gateStatus.vollmacht_required && (
              <div className={`px-3 py-1 rounded-full ${gateStatus.vollmacht_signed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {gateStatus.vollmacht_signed ? '✓' : '✗'} Vollmacht
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                  activeTab === tab.key
                    ? 'text-brand-green border-b-2 border-brand-green bg-brand-green/5'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-8">
            {activeTabComponent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCaseDetailPage;
